import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { userManagementApiClient } from "../api/userManagementApiClient";
import {
  parsePermissions,
  isPermissionAllowed,
  canManageUserByRole,
} from "../rbac";
import { readUserInitData, clearUserInitData } from "../utils/userInitStorage";
import { getLogger } from "@hoteltradergit/ht-telemetry-ui-lib";

import { DEFAULT_PERMISSION_CONTEXT } from "./permissionContextDefaults";

export const PermissionContext = createContext(DEFAULT_PERMISSION_CONTEXT);

let activationInFlight = false;

export const PermissionProvider = React.memo(function PermissionProvider({
  children,
}) {
  const initDataRef = useRef(readUserInitData());

  const [userInfo, setUserInfo] = useState(() => {
    const d = initDataRef.current;
    return d
      ? d.userInfo
      : { firstName: "", lastName: "", imageUrl: "", email: "" };
  });
  const [loading, setLoading] = useState(() => !initDataRef.current);
  const [permissions, setPermissions] = useState(() => {
    const d = initDataRef.current;
    return d
      ? { allowSet: d.allowSet, denySet: d.denySet }
      : { allowSet: new Set(), denySet: new Set() };
  });
  // Raw rolePermissions array — exposed so @hoteltradergit/user-management can do
  // its own appId-aware parsing without a duplicate API call. Hydrate from the Callback
  // pre-fetch cache too; otherwise the cache-hit path skips the fetch and the package sees
  // [] on first render, hiding its nav (Manage Users) until a later refetch.
  const [rolePermissions, setRolePermissions] = useState(
    () => initDataRef.current?.rolePermissions ?? []
  );
  const isFetchingRef = useRef(false);

  const fetchUserInfo = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      const response = await userManagementApiClient.getUserPermissions();
      const user = response?.data?.data || response?.data;

      if (user) {
        setUserInfo({
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          imageUrl: user.profileImageSmall || "",
          email: user.email || "",
        });

        const parsed = parsePermissions(user.rolePermissions);
        setPermissions({
          allowSet: parsed.allowSet,
          denySet: parsed.denySet,
        });
        setRolePermissions(user.rolePermissions ?? []);

        // Activate pending users on first login
        if (user?.status?.toUpperCase() === "PENDING" && !activationInFlight) {
          activationInFlight = true;
          try {
            await userManagementApiClient.activateUser();
          } catch {
            // Error on activating
          } finally {
            activationInFlight = false;
          }
        }
      }
    } catch (error) {
      getLogger().error("Failed to fetch user info:", {
        type: "Error",
        page_url: window.location.href,
        error_desc: error,
      });
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, []);

  // Initial fetch on mount (skip if hydrated from Callback pre-fetch)
  useEffect(() => {
    if (initDataRef.current) {
      clearUserInitData();
      initDataRef.current = null;
      return;
    }
    fetchUserInfo();
  }, [fetchUserInfo]);

  // Listen for profile image updates
  useEffect(() => {
    const handleProfileImageUpdate = () => {
      fetchUserInfo();
    };
    globalThis.addEventListener(
      "profileImageUpdated",
      handleProfileImageUpdate
    );
    return () =>
      globalThis.removeEventListener(
        "profileImageUpdated",
        handleProfileImageUpdate
      );
  }, [fetchUserInfo]);

  // Refetch on globalThis focus (tab switch back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchUserInfo();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [fetchUserInfo]);

  // Refetch on 403 — axios dispatches this event instead of logging out
  useEffect(() => {
    const handlePermissionRefetch = () => {
      fetchUserInfo();
    };
    globalThis.addEventListener("permissionRefetch", handlePermissionRefetch);
    return () =>
      globalThis.removeEventListener(
        "permissionRefetch",
        handlePermissionRefetch
      );
  }, [fetchUserInfo]);

  // 45-minute refresh interval (backstop)
  useEffect(() => {
    const intervalTimeout = import.meta.env
      .VITE_PERMISSION_REFRESH_INTERVAL_MINUTES
      ? Number.parseInt(
          import.meta.env.VITE_PERMISSION_REFRESH_INTERVAL_MINUTES,
          10
        ) *
        60 *
        1000
      : 45 * 60 * 1000; // Default to 45 minutes
    const intervalId = setInterval(() => {
      fetchUserInfo();
    }, intervalTimeout);
    return () => clearInterval(intervalId);
  }, [fetchUserInfo]);

  const { allowSet, denySet } = permissions;

  const hasPermission = useCallback(
    (permKey) => isPermissionAllowed(permKey, allowSet, denySet),
    [allowSet, denySet]
  );

  const canManageUser = useCallback(
    (targetRoleName) => canManageUserByRole(targetRoleName, allowSet, denySet),
    [allowSet, denySet]
  );

  const contextValue = useMemo(
    () => ({
      userInfo,
      loading,
      hasPermission,
      canManageUser,
      rolePermissions,
      refreshUserInfo: fetchUserInfo,
    }),
    [
      userInfo,
      loading,
      hasPermission,
      canManageUser,
      rolePermissions,
      fetchUserInfo,
    ]
  );

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
});

export const usePermissions = () => useContext(PermissionContext);
